import React, { useState } from "react";
import { Typography, Alert, Box } from "@mui/material";
import { guidelinesApi } from "../../api";
import { ConfirmDialog } from "../../components/shared";

interface DeleteConfirmDialogProps {
  open: boolean;
  guidelineId: string;
  guidelineName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  guidelineId,
  guidelineName,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      await guidelinesApi.delete(guidelineId);
      onSuccess();
    } catch (err: any) {
      console.error("Failed to delete guideline:", err);
      setError(
        err.response?.data?.detail || "Failed to delete guideline. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      variant="destructive"
      title="Delete Guideline"
      body="Are you sure you want to delete this guideline?"
      confirmLabel="Delete Guideline"
      onConfirm={handleDelete}
      onClose={onClose}
      loading={loading}
    >
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          mt: 1.5,
          p: 1.5,
          bgcolor: "action.hover",
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {guidelineName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ID: {guidelineId}
        </Typography>
      </Box>
      <Alert severity="warning" sx={{ mt: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          This action cannot be undone.
        </Typography>
        <Typography variant="body2">This will permanently delete:</Typography>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          <li>The guideline document</li>
          <li>All associated sections</li>
          <li>All associated chunks</li>
          <li>All associated embeddings</li>
        </ul>
      </Alert>
    </ConfirmDialog>
  );
};

export default DeleteConfirmDialog;
